import axios from 'axios';
import type { CancelTokenSource } from 'axios';

import type { ServerError } from '../../models';
import type { GetReferralOfferResponse, GetSignupReferralResponse, ReferralOfferResult } from '../../models/reqres';
import { mapReferralOfferResponse } from '../../utils/referralOffer';
import { exchangeApi, handleErrorCallback, noop } from '../core';

/** V2 pre-signup referral preview: separates flat vs variable rewards and exposes the trade threshold. */
export function getReferralOffer(
    referralCode: string,
    onSuc: (result: ReferralOfferResult) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<GetReferralOfferResponse>(`/rewards/referrals/offer/${encodeURIComponent(referralCode)}`, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(mapReferralOfferResponse(response.data));
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getSignupReferral(
    userId: string,
    onSuc: (referralInfos: GetSignupReferralResponse) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<GetSignupReferralResponse>(`/users/${userId}/referral_code`, { cancelToken: cancelRef.token })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function redeemPromoCode(
    code: string,
    onSuc: () => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
    onLog?: (err: ServerError) => void,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .post('/rewards/promo-codes/redeem', { code }, { cancelToken: cancelRef.token })
        .then(onSuc)
        .catch((error) => handleErrorCallback(error, onErr, onLog))
        .finally(onFinally);

    return cancelRef;
}
